import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { dispatch, useSelector } from '../../redux/store';
import { getDentallyConfigs } from '../../redux/slices/practiceIntegration';
import OpenAppointmentPopup from '../fullScreenPopUps/OpenAppointmentPopup';
import BookingModeToggle from '../dentally/BookingModeToggle';
import DentallyFollowUpBookingDialog from '../dentally/DentallyFollowUpBookingDialog';

const asId = (value) => (value == null ? '' : String(value));

export default function AppointmentBookingRouter({
  open,
  onClose,
  onBooked,
  practiceId,
  existingLead,
  existingTreatmentId,
  treatmentData,
  inputRef,
  headerButtons,
  type,
}) {
  const permissionsState = useSelector((state) => state.permissions || {});
  const dentallyEnabledGlobal = useSelector((state) => state.practice?.dentallyEnabledGlobal === true);
  const integration = useSelector((state) => state.practiceIntegration || {});

  const expectedPractice = asId(practiceId);
  const permissionPractice = asId(permissionsState.practiceId);
  const permissionTarget = asId(permissionsState.loadingPracticeId);
  const permissionScopeCurrent = Boolean(expectedPractice)
    && !permissionsState.loading
    && permissionPractice === expectedPractice
    && (!permissionTarget || permissionTarget === expectedPractice);
  const canManageDentally = permissionScopeCurrent
    && permissionsState.permissions?.['dentally-integration'] === 'manage';

  const configScopeCurrent = asId(integration.dentallyConfigPracticeId) === expectedPractice;
  const configResults = integration.dentallyConfigs?.results || [];
  const activeConfig = configScopeCurrent
    ? configResults.find((config) => config?.is_active === true) || null
    : null;
  let configLoadState = 'not_requested';
  if (configScopeCurrent) {
    if (integration.dentallyConfigLoadState) configLoadState = integration.dentallyConfigLoadState;
    else if (integration.dentallyLoading) configLoadState = 'loading';
    else if (integration.dentallyConfigs) configLoadState = 'loaded';
  }
  const configTerminal = ['loaded', 'forbidden', 'error'].includes(configLoadState);
  const dentallyEligible = Boolean(
    canManageDentally
    && dentallyEnabledGlobal
    && configScopeCurrent
    && !integration.dentallyLoading
    && configLoadState === 'loaded'
    && activeConfig
  );

  const leadId = existingLead?.lead_id
    || existingLead?.lead?.id
    || existingLead?.id
    || treatmentData?.lead
    || null;
  const normalizedLead = useMemo(() => ({
    ...(existingLead || {}),
    id: leadId,
    lead_contact: existingLead?.lead_contact || {
      email: existingLead?.email || '',
      phone: existingLead?.phone || '',
    },
  }), [existingLead, leadId]);
  const treatmentId = existingTreatmentId || treatmentData?.id || null;
  const scopeKey = `${open ? 'open' : 'closed'}:${expectedPractice}:${asId(leadId)}:${asId(treatmentId)}`;

  const [selection, setSelection] = useState({ scopeKey: null, provider: 'crm', explicit: false });
  const [busy, setBusy] = useState(false);
  const activeScopeRef = useRef(scopeKey);
  const completedScopeRef = useRef(null);
  const mountedRef = useRef(false);
  const attemptProviderRef = useRef(null);
  activeScopeRef.current = scopeKey;

  let provider = dentallyEligible ? 'dentally' : 'crm';
  const { provider: selectedProvider } = selection;
  if (selection.scopeKey === scopeKey) provider = selectedProvider;
  const attemptProvider = attemptProviderRef.current?.scopeKey === scopeKey
    ? attemptProviderRef.current.provider
    : null;
  if (attemptProvider) provider = attemptProvider;
  const useDentally = provider === 'dentally' && (dentallyEligible || attemptProvider === 'dentally');

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    completedScopeRef.current = null;
    attemptProviderRef.current = null;
    setBusy(false);
    setSelection({ scopeKey, provider: dentallyEligible ? 'dentally' : 'crm', explicit: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  useEffect(() => {
    setSelection((current) => {
      if (current.scopeKey !== scopeKey || current.explicit) return current;
      if (attemptProviderRef.current?.scopeKey === scopeKey) return current;
      let nextProvider = current.provider;
      if (dentallyEligible) nextProvider = 'dentally';
      else if (configTerminal) nextProvider = 'crm';
      return nextProvider === current.provider ? current : { ...current, provider: nextProvider };
    });
  }, [configTerminal, dentallyEligible, scopeKey]);

  useEffect(() => {
    if (!open || !practiceId || !canManageDentally || !dentallyEnabledGlobal) return;
    if (configLoadState === 'not_requested') {
      dispatch(getDentallyConfigs(practiceId));
    }
  }, [
    open,
    practiceId,
    canManageDentally,
    dentallyEnabledGlobal,
    configScopeCurrent,
    configLoadState,
  ]);

  const isCurrentScope = useCallback((capturedScope = scopeKey) => (
    mountedRef.current && activeScopeRef.current === capturedScope
  ), [scopeKey]);

  const emitBooked = useCallback((outcome, capturedScope = scopeKey) => {
    if (!isCurrentScope(capturedScope) || completedScopeRef.current === capturedScope) return false;
    completedScopeRef.current = capturedScope;
    onBooked?.(outcome);
    return true;
  }, [isCurrentScope, onBooked, scopeKey]);

  const handleBusyChange = useCallback((nextBusy) => {
    if (!isCurrentScope(scopeKey)) return;
    const isBusy = Boolean(nextBusy);
    attemptProviderRef.current = isBusy ? { scopeKey, provider } : null;
    setBusy(isBusy);
  }, [isCurrentScope, provider, scopeKey]);

  if (!open || !leadId || !treatmentId) return null;

  const modeControl = dentallyEligible ? (
    <BookingModeToggle
      useDentally={useDentally}
      disabled={busy}
      onChange={(nextDentally) => {
        if (!busy) {
          setSelection({ scopeKey, provider: nextDentally ? 'dentally' : 'crm', explicit: true });
        }
      }}
    />
  ) : null;

  if (useDentally) {
    return (
      <DentallyFollowUpBookingDialog
        key={scopeKey}
        open={open}
        existingLead={normalizedLead}
        existingTreatmentId={Number(treatmentId)}
        practiceId={practiceId}
        exportTrigger={activeConfig?.dentally_export_trigger || 'APPOINTMENT'}
        modeControl={modeControl}
        onBusyChange={handleBusyChange}
        onClose={(booked, result) => {
          if (!isCurrentScope(scopeKey)) return;
          if (!booked) {
            onClose?.('close');
            return;
          }
          if (result?.isDeferred || !result?.crmAppointmentId) {
            onClose?.('deferred', result);
            return;
          }
          const accepted = emitBooked({
            booked: true,
            provider: 'dentally',
            crmAppointmentId: result.crmAppointmentId,
            dentallyOutcome: result.dentallyOutcome
              || (result.dentallyWarning ? 'reconciliation_required' : 'created'),
          });
          if (accepted) onClose?.('finish', result);
        }}
      />
    );
  }

  return (
    <OpenAppointmentPopup
      key={scopeKey}
      open={open}
      headerButtons={headerButtons}
      onClose={(...args) => { if (isCurrentScope(scopeKey)) onClose?.(...args); }}
      onBooked={(result) => emitBooked({ booked: true, provider: 'crm', ...result })}
      isScopeCurrent={() => isCurrentScope(scopeKey)}
      onBusyChange={handleBusyChange}
      modeControl={modeControl}
      treatmentData={treatmentData}
      inputRef={inputRef}
      type={type}
    />
  );
}

AppointmentBookingRouter.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBooked: PropTypes.func,
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  existingLead: PropTypes.object,
  existingTreatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  treatmentData: PropTypes.object,
  inputRef: PropTypes.any,
  headerButtons: PropTypes.bool,
  type: PropTypes.string,
};
